import React, { useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import HeartIcon from 'mdi-react/HeartIcon';
import HeartOutlineIcon from 'mdi-react/HeartOutlineIcon';
import BellIcon from 'mdi-react/BellIcon';
import BellOutlineIcon from 'mdi-react/BellOutlineIcon';
import CompareIcon from 'mdi-react/ScaleBalanceIcon';
import { useAppContext } from '@prisjakt/ui-app-context';
import Dropdown from '@prisjakt/ui-dropdown-new';

import LoginPromoModal from '../../../../../../components/modal/login-promo';
import alertActions from '../../../../../sidebar/actions/alert';
import listActions from '../../../../../sidebar/actions/lists';

import { AppState } from '../../../../../../reducers';
import { ProductPageQuery } from '../../../../../../types';
import {
  SelectedList,
  DeletedLists,
  UserProductAlert,
  UserProductAlerts,
  SelectList,
  AddListItemRequest,
  RemoveAlertRequest,
  OpenAlerts,
} from '../../../../../sidebar/types';

import * as S from './styles';

type OwnProps = {
  horizontal?: boolean;
  isDesktop?: boolean;
  product: NonNullable<ProductPageQuery['product']>;
  buttonStyle?: ButtonStyle;
  className?: string;
  onAction: (type: string) => void;
};

type StateProps = {
  isAuthenticated: boolean;
  me: {
    lists: {
      nodes: [{ id: number; name: string }];
    };
  };
  userProductAlerts: UserProductAlerts;
  deletedLists: DeletedLists;
  selectedList: SelectedList;
};

type DispatchProps = {
  addListItemRequest: AddListItemRequest;
  selectList: SelectList;
  openAlerts: OpenAlerts;
  removeAlertRequest: RemoveAlertRequest;
};

export type ButtonStyle = 'square' | 'circle';

type Props = OwnProps & StateProps & DispatchProps;

const ProductActionButtons = ({
  className,
  horizontal = false,
  isDesktop = false,
  buttonStyle = 'square',
  product,
  isAuthenticated,
  me,
  deletedLists,
  selectedList,
  userProductAlerts,
  onAction,
  addListItemRequest,
  selectList,
  openAlerts,
  removeAlertRequest,
}: Props) => {
  const { t, linkTo } = useAppContext();

  // Highlights the heart if the product was recently added to a list.
  // Kind of an ugly hack; should be fixed once we can get this information
  // from GraphQL.
  const [addedToList, setAddedToListState] = useState(false);
  const [showLoginPromo, setLoginPromoState] = useState(false);

  const toggleLoginPromo = useCallback(() => {
    setLoginPromoState(!showLoginPromo);
  }, [showLoginPromo]);

  const addToList = useCallback(() => {
    onAction('list-add');
    addListItemRequest(product, isDesktop);

    setAddedToListState(true);
  }, [addListItemRequest, isDesktop, onAction, product]);

  const latestLists =
    me &&
    me.lists &&
    me.lists.nodes
      .filter(list => deletedLists[list.id] !== true)
      .sort(a => {
        if (a.id === selectedList) {
          return 1;
        }

        return 0;
      });

  const handleListButtonClick = useCallback(
    e => {
      e.stopPropagation();
      addToList();
    },
    [addToList]
  );

  const userAlert = userProductAlerts.find(
    (alert: UserProductAlert) => alert.productId === product.id
  );
  const alertId = userAlert && userAlert.alertId;

  const removeAlert = useCallback(() => {
    if (!alertId) return;

    onAction('alert-remove');
    openAlerts();
    removeAlertRequest(alertId, product.id);
  }, [alertId, onAction, openAlerts, product.id, removeAlertRequest]);

  const editAlert = useCallback(() => {
    openAlerts();
    onAction('alert-create');
  }, [onAction, openAlerts]);

  return (
    <S.ProductActionButtons
      buttonStyle={buttonStyle}
      horizontal={horizontal}
      className={className}
    >
      {isAuthenticated ? (
        <Dropdown
          variant="secondary"
          grow="right"
          showArrow={false}
          toggleElement={
            // eslint-disable-next-line react-perf/jsx-no-jsx-as-prop
            <ListsButton
              buttonStyle={buttonStyle}
              isActive={!!addedToList}
              onClick={
                latestLists.length > 0 ? undefined : handleListButtonClick
              }
            />
          }
        >
          {latestLists.map(list => (
            <S.DropdownItem
              onClick={() => {
                selectList(list.id);
                addToList();
              }}
              key={list.id}
            >
              {list.name}
            </S.DropdownItem>
          ))}
        </Dropdown>
      ) : (
        <>
          {showLoginPromo && (
            <LoginPromoModal
              isOpen={showLoginPromo}
              onRequestClose={toggleLoginPromo}
            />
          )}
          <ListsButton
            buttonStyle={buttonStyle}
            isActive={!!addedToList}
            onClick={toggleLoginPromo}
          />
        </>
      )}

      {alertId && isAuthenticated ? (
        <Dropdown
          showArrow={false}
          variant="secondary"
          grow="right"
          toggleElement={
            // eslint-disable-next-line react-perf/jsx-no-jsx-as-prop
            <S.ActionButton
              buttonStyle={buttonStyle}
              data-test="CreateAlertButton"
            >
              <S.Tooltip
                title={t('product', 'action-button.alert-tooltip')}
                horizontal={horizontal}
                width={180}
              >
                <S.Button
                  buttonStyle={buttonStyle}
                  variant={
                    buttonStyle === 'circle' ? 'iconOutlined' : 'outlined'
                  }
                  size={buttonStyle === 'circle' ? 'medium' : 'small'}
                  color={buttonStyle === 'circle' ? 'primary' : 'positive'}
                  active
                  icon={<BellIcon size={20} />}
                  iconOutlined={<BellOutlineIcon size={20} />}
                  component={<div />}
                >
                  <S.ButtonText>
                    {t('product', 'alert.create-button')}
                  </S.ButtonText>
                </S.Button>
              </S.Tooltip>
            </S.ActionButton>
          }
        >
          <S.DropdownItem onClick={removeAlert}>
            {t('product', 'alert.delete-button')}
          </S.DropdownItem>
          <S.DropdownItem onClick={editAlert}>
            {t('product', 'alert.edit-button')}
          </S.DropdownItem>
        </Dropdown>
      ) : (
        <S.ActionButton buttonStyle={buttonStyle} data-test="CreateAlertButton">
          <S.Tooltip
            title={t('product', 'action-button.alert-tooltip')}
            width={180}
          >
            <S.Button
              buttonStyle={buttonStyle}
              color="primary"
              variant={buttonStyle === 'circle' ? 'iconOutlined' : 'outlined'}
              size={buttonStyle === 'circle' ? 'medium' : 'small'}
              icon={<BellIcon size={20} />}
              iconOutlined={<BellOutlineIcon size={20} />}
              component={<div />}
              ariaLabel={t('product', 'alert.create-button')}
              onClick={() => {
                onAction('alert-create');
              }}
            >
              <S.ButtonText>{t('product', 'alert.create-button')}</S.ButtonText>
            </S.Button>
          </S.Tooltip>
        </S.ActionButton>
      )}

      <S.ActionButton buttonStyle={buttonStyle} data-test="CompareButton">
        <S.Tooltip
          title={t('product', 'action-button.compare-tooltip')}
          width={180}
          align="right"
        >
          <S.Button
            buttonStyle={buttonStyle}
            variant={buttonStyle === 'circle' ? 'iconOutlined' : 'outlined'}
            size={buttonStyle === 'circle' ? 'medium' : 'small'}
            color="primary"
            icon={<CompareIcon size={20} />}
            iconOutlined={<CompareIcon size={20} />}
            ariaLabel={t('product', 'product-compare.compare')}
            component={
              // eslint-disable-next-line react-perf/jsx-no-jsx-as-prop
              <Link
                to={linkTo('productCompare', {
                  category: product.category,
                  products: [product],
                })}
                rel="nofollow"
              />
            }
          >
            <S.ButtonText>
              {t('product', 'product-compare.compare')}
            </S.ButtonText>
          </S.Button>
        </S.Tooltip>
      </S.ActionButton>
    </S.ProductActionButtons>
  );
};

type ListsButtonProps = {
  component?: React.ReactElement;
  buttonStyle: ButtonStyle;
  isActive: boolean;
  onClick?: (e: React.MouseEvent) => void;
};
const ListsButton = ({
  component,
  buttonStyle,
  isActive,
  onClick,
}: ListsButtonProps) => {
  const { t } = useAppContext();

  return (
    <S.ActionButton buttonStyle={buttonStyle} data-test="SaveToListButton">
      <S.Tooltip
        title={t('product', 'action-button.list-tooltip')}
        align="left"
        width={180}
      >
        <S.Button
          buttonStyle={buttonStyle}
          variant={buttonStyle === 'circle' ? 'iconOutlined' : 'outlined'}
          size={buttonStyle === 'circle' ? 'medium' : 'small'}
          color={isActive && buttonStyle === 'square' ? 'positive' : 'primary'}
          icon={<HeartIcon />}
          iconOutlined={<HeartOutlineIcon />}
          active={isActive}
          component={component}
          ariaLabel={t('common', 'button.save')}
          onClick={onClick}
        >
          <S.ButtonText>{t('common', 'button.save')}</S.ButtonText>
        </S.Button>
      </S.Tooltip>
    </S.ActionButton>
  );
};

const mapStateToProps = (state: AppState): StateProps => ({
  me: state.areas.account.me,
  isAuthenticated: state.areas.account.isAuthenticated,
  userProductAlerts: state.areas.sidebar.alerts.products,
  deletedLists: state.areas.sidebar.deletedLists,
  selectedList: state.areas.sidebar.selectedList,
});

const dispatchActions = {
  selectList: listActions.selectList,
  addListItemRequest: listActions.addListItemRequest,
  openAlerts: alertActions.openAlerts,
  removeAlertRequest: alertActions.removeAlertRequest,
};

export default connect(mapStateToProps, dispatchActions)(ProductActionButtons);
